import { useEffect, useMemo, useRef, useState } from "react";
import { Merchandise, MerchandiseCatalogItem } from "types";
import { VariantCatalogIdentity } from "./variantGroupItemInventoryPayloadHelper";
import { getVariantCatalogItems, VariantCatalogItem } from "./VariantTableHelper";
import { getPublicVariantCount } from "@/models/Merchandise";

const getCatalogItemPrice = (item: VariantCatalogItem): number | null => {
    return item.prices?.perUnit?.price ?? item.price ?? null;
};

export const useVariantCatalogData = (merchandise: Merchandise, fetchedCatalogItems?: MerchandiseCatalogItem[]) => {
    const derivedCatalogItems = useMemo(
        () => getVariantCatalogItems(merchandise, fetchedCatalogItems),
        [merchandise, fetchedCatalogItems],
    );

    const [catalogItems, setCatalogItems] = useState<VariantCatalogItem[]>(derivedCatalogItems);
    const [expandedItems, setExpandedItems] = useState<Set<string>>(
        () => new Set(derivedCatalogItems.filter((item) => !item.isLeaf && item.id).map((item) => item.id as string)),
    );

    useEffect(() => {
        setCatalogItems(derivedCatalogItems);
        setExpandedItems(
            new Set(derivedCatalogItems.filter((item) => !item.isLeaf && item.id).map((item) => item.id as string)),
        );
    }, [derivedCatalogItems]);

    const originalCatalogIdentityById = useMemo(() => {
        return new Map(
            derivedCatalogItems
                .filter((item) => item.isLeaf && Boolean(item.id))
                .map((item) => [
                    item.id as string,
                    {
                        sku: item.sku ?? "",
                        barcode: item.barcode ?? "",
                        price: getCatalogItemPrice(item),
                    },
                ]),
        );
    }, [derivedCatalogItems]);

    const originalCatalogIdentityByIdRef = useRef<Map<string, VariantCatalogIdentity>>(originalCatalogIdentityById);

    useEffect(() => {
        originalCatalogIdentityByIdRef.current = originalCatalogIdentityById;
    }, [originalCatalogIdentityById]);

    const publicVariantCount = useMemo(() => {
        const leafCatalogItems = catalogItems.filter((item) => item.isLeaf) as MerchandiseCatalogItem[];
        return getPublicVariantCount(leafCatalogItems);
    }, [catalogItems]);

    const leafItemIds = useMemo(() => {
        return catalogItems.filter((item) => item.isLeaf && Boolean(item.id)).map((item) => item.id as string);
    }, [catalogItems]);

    return {
        catalogItems,
        setCatalogItems,
        expandedItems,
        setExpandedItems,
        originalCatalogIdentityByIdRef,
        publicVariantCount,
        leafItemIds,
    };
};
